import React, { useState, useEffect, useMemo, useTransition } from 'react';
import { Plus, Search, CheckSquare, Square, Pencil, Trash2, Check, X, AlertCircle, GitMerge } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription } from '../../ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { supabase } from '../../../lib/supabaseClient';
import { useSettingsData } from '../../../contexts/SettingsDataContext';

const CATEGORIES = [
  { value: '', label: 'No Category' },
  { value: 'developer', label: 'Developer' },
  { value: 'engineer', label: 'Engineer' },
  { value: 'architect', label: 'Architect' },
  { value: 'management', label: 'Management' },
  { value: 'analyst', label: 'Analyst' },
  { value: 'consultant', label: 'Consultant' },
  { value: 'specialist', label: 'Specialist' },
  { value: 'support', label: 'Support' },
];

const PositionsManagement = () => {
  // Use cached data from context
  const { positions: contextPositions, loading: contextLoading, refreshPositions } = useSettingsData();
  const positions = contextPositions || [];
  const loading = contextLoading && !contextPositions;

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isPending, startTransition] = useTransition();
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const [selectedPositionIds, setSelectedPositionIds] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);
  const [formData, setFormData] = useState({ name: '', category: '', status: 'active', mapped_to_position_id: null });
  const [positionToDelete, setPositionToDelete] = useState(null);

  // Bulk map state
  const [showBulkMapDialog, setShowBulkMapDialog] = useState(false);
  const [bulkMapTargetId, setBulkMapTargetId] = useState('');
  const [bulkMapping, setBulkMapping] = useState(false);

  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  // Debounce search term (non-blocking with useTransition)
  useEffect(() => {
    const timer = setTimeout(() => {
      startTransition(() => {
        setDebouncedSearchTerm(searchTerm);
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const filteredPositions = useMemo(() => {
    let filtered = positions;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(pos => pos.status === statusFilter);
    }

    if (categoryFilter !== 'all') {
      if (categoryFilter === 'none') {
        filtered = filtered.filter(pos => !pos.category);
      } else {
        filtered = filtered.filter(pos => pos.category === categoryFilter);
      }
    }

    if (debouncedSearchTerm) {
      const lowerSearch = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter(pos =>
        pos.name.toLowerCase().includes(lowerSearch)
      );
    }

    filtered.sort((a, b) => {
      if (a.status === 'pending' && b.status !== 'pending') return -1;
      if (a.status !== 'pending' && b.status === 'pending') return 1;
      return a.name.localeCompare(b.name);
    });

    return filtered;
  }, [positions, statusFilter, categoryFilter, debouncedSearchTerm]);

  const handleAddPosition = async () => {
    if (formData.status === 'mapping' && !formData.mapped_to_position_id) {
      setErrorMessage('Please select a position to map to');
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/positions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          category: formData.category || null,
          status: formData.status,
          mapped_to_position_id: formData.status === 'mapping' ? formData.mapped_to_position_id : null
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create position');
      }

      setSuccessMessage(`Position "${data.data.name}" created successfully!`);
      setShowAddDialog(false);
      setFormData({ name: '', category: '', status: 'active', mapped_to_position_id: null });
      await refreshPositions();
    } catch (error) {
      console.error('Error adding position:', error);
      setErrorMessage(error.message);
    }
  };

  const handleUpdatePosition = async () => {
    if (formData.status === 'mapping' && !formData.mapped_to_position_id) {
      setErrorMessage('Please select a position to map to');
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/positions/${editingPosition.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          category: formData.category || null,
          status: formData.status,
          mapped_to_position_id: formData.status === 'mapping' ? formData.mapped_to_position_id : null
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update position');
      }

      setSuccessMessage(`Position "${data.data.name}" updated successfully!`);
      setEditingPosition(null);
      setFormData({ name: '', category: '', status: 'active', mapped_to_position_id: null });
      await refreshPositions();
    } catch (error) {
      console.error('Error updating position:', error);
      setErrorMessage(error.message);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedPositionIds.length === 0) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/positions/bulk-status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          position_ids: selectedPositionIds,
          status: 'active'
        })
      });

      if (!response.ok) throw new Error('Failed to approve positions');

      setSuccessMessage(`Successfully approved ${selectedPositionIds.length} position(s)!`);
      setSelectedPositionIds([]);
      await refreshPositions();
    } catch (error) {
      console.error('Error bulk approving:', error);
      setErrorMessage(error.message);
    }
  };

  const handleBulkReject = async () => {
    if (selectedPositionIds.length === 0) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/positions/bulk-status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          position_ids: selectedPositionIds,
          status: 'rejected'
        })
      });

      if (!response.ok) throw new Error('Failed to reject positions');

      setSuccessMessage(`Successfully rejected ${selectedPositionIds.length} position(s)!`);
      setSelectedPositionIds([]);
      await refreshPositions();
    } catch (error) {
      console.error('Error bulk rejecting:', error);
      setErrorMessage(error.message);
    }
  };

  const handleBulkMap = async () => {
    if (!bulkMapTargetId) {
      setErrorMessage('Please select a target position');
      return;
    }

    try {
      setBulkMapping(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/positions/bulk-map`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          position_ids: selectedPositionIds,
          mapped_to_position_id: bulkMapTargetId
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to map positions');
      }

      const targetPosition = positions.find(p => p.id === bulkMapTargetId);
      setSuccessMessage(
        `Successfully mapped ${data.updated} position(s) to "${targetPosition?.name}"`
      );

      setShowBulkMapDialog(false);
      setBulkMapTargetId('');
      setSelectedPositionIds([]);
      await refreshPositions();
    } catch (error) {
      console.error('Error mapping positions:', error);
      setErrorMessage(error.message);
    } finally {
      setBulkMapping(false);
    }
  };

  const handleDeletePosition = async () => {
    if (!positionToDelete) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/positions/${positionToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete position');
      }

      setSuccessMessage(`Position "${positionToDelete.name}" deleted successfully!`);
      setPositionToDelete(null);
      await refreshPositions();
    } catch (error) {
      console.error('Error deleting position:', error);
      setErrorMessage(error.message);
    }
  };

  const toggleSelection = (positionId) => {
    setSelectedPositionIds(prev =>
      prev.includes(positionId)
        ? prev.filter(id => id !== positionId)
        : [...prev, positionId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedPositionIds.length === filteredPositions.length) {
      setSelectedPositionIds([]);
    } else {
      setSelectedPositionIds(filteredPositions.map(pos => pos.id));
    }
  };

  const activePositions = positions.filter(pos => pos.status === 'active');
  const pendingCount = positions.filter(pos => pos.status === 'pending').length;

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      rejected: 'bg-red-100 text-red-800',
      mapping: 'bg-blue-100 text-blue-800'
    };
    return <Badge className={styles[status] || 'bg-gray-100 text-gray-800'}>{status}</Badge>;
  };

  return (
    <div className="space-y-6">
      {successMessage && (
        <Alert className="bg-green-50 border-green-200">
          <Check className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">{successMessage}</AlertDescription>
        </Alert>
      )}

      {errorMessage && (
        <Alert className="bg-red-50 border-red-200">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">{errorMessage}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Position Management</CardTitle>
              <CardDescription>
                Manage position titles, approve pending positions, and set up position mappings
              </CardDescription>
            </div>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Position
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search positions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border rounded-md"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending ({pendingCount})</option>
              <option value="rejected">Rejected</option>
              <option value="mapping">Mapping</option>
            </select>

            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-2 border rounded-md"
            >
              <option value="all">All Categories</option>
              <option value="none">No Category</option>
              {CATEGORIES.filter(c => c.value).map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>

          {selectedPositionIds.length > 0 && (
            <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <span className="text-sm font-medium text-blue-900">
                {selectedPositionIds.length} position(s) selected
              </span>
              <Button size="sm" variant="outline" onClick={handleBulkApprove}>
                <Check className="h-4 w-4 mr-1" />
                Approve
              </Button>
              <Button size="sm" variant="outline" onClick={handleBulkReject}>
                <X className="h-4 w-4 mr-1" />
                Reject
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowBulkMapDialog(true)}>
                <GitMerge className="h-4 w-4 mr-1" />
                Map Selected
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelectedPositionIds([])}>
                Clear Selection
              </Button>
            </div>
          )}

          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading positions...</div>
          ) : filteredPositions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No positions found</div>
          ) : (
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <button onClick={toggleSelectAll}>
                        {selectedPositionIds.length === filteredPositions.length ? (
                          <CheckSquare className="h-4 w-4" />
                        ) : (
                          <Square className="h-4 w-4" />
                        )}
                      </button>
                    </TableHead>
                    <TableHead>Position</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Mapped To</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPositions.map((position) => {
                    const mappedToPosition = position.mapped_to_position_id
                      ? positions.find(p => p.id === position.mapped_to_position_id)
                      : null;

                    return (
                      <TableRow key={position.id}>
                        <TableCell>
                          <button onClick={() => toggleSelection(position.id)}>
                            {selectedPositionIds.includes(position.id) ? (
                              <CheckSquare className="h-4 w-4 text-blue-600" />
                            ) : (
                              <Square className="h-4 w-4" />
                            )}
                          </button>
                        </TableCell>
                        <TableCell className="font-medium">{position.name}</TableCell>
                        <TableCell>
                          {position.category ? (
                            <Badge variant="outline">{position.category}</Badge>
                          ) : (
                            <span className="text-gray-400 text-sm">None</span>
                          )}
                        </TableCell>
                        <TableCell>{getStatusBadge(position.status)}</TableCell>
                        <TableCell>
                          {mappedToPosition ? (
                            <div className="flex items-center gap-1 text-sm">
                              <GitMerge className="h-3 w-3 text-blue-600" />
                              <span>{mappedToPosition.name}</span>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingPosition(position);
                                setFormData({
                                  name: position.name,
                                  category: position.category || '',
                                  status: position.status,
                                  mapped_to_position_id: position.mapped_to_position_id || null
                                });
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setPositionToDelete(position)}
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showAddDialog || editingPosition !== null} onOpenChange={(open) => {
        if (!open) {
          setShowAddDialog(false);
          setEditingPosition(null);
          setFormData({ name: '', category: '', status: 'active', mapped_to_position_id: null });
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingPosition ? 'Edit Position' : 'Add Position'}</DialogTitle>
            <DialogDescription>
              {editingPosition ? 'Update position details' : 'Create a new position title'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Position Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Senior Software Developer"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-3 py-2 border rounded-md"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-medium">Status *</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
                <option value="mapping">Mapping (maps to another position)</option>
              </select>
            </div>

            {formData.status === 'mapping' && (
              <div>
                <label className="text-sm font-medium">Map To Position *</label>
                <select
                  value={formData.mapped_to_position_id || ''}
                  onChange={(e) => setFormData({ ...formData, mapped_to_position_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">Select canonical position...</option>
                  {activePositions.map(pos => (
                    <option key={pos.id} value={pos.id}>{pos.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAddDialog(false);
              setEditingPosition(null);
              setFormData({ name: '', category: '', status: 'active', mapped_to_position_id: null });
            }}>
              Cancel
            </Button>
            <Button onClick={editingPosition ? handleUpdatePosition : handleAddPosition}>
              {editingPosition ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={positionToDelete !== null} onOpenChange={() => setPositionToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Position</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{positionToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPositionToDelete(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeletePosition}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Map Dialog */}
      <Dialog open={showBulkMapDialog} onOpenChange={(open) => !open && setShowBulkMapDialog(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitMerge className="h-5 w-5 text-blue-600" />
              Bulk Map Positions
            </DialogTitle>
            <DialogDescription>
              Map {selectedPositionIds.length} selected position{selectedPositionIds.length > 1 ? 's' : ''} to a single canonical position. All will be set to "mapping" status.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Selected Positions ({selectedPositionIds.length})</label>
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-md max-h-32 overflow-y-auto">
                <div className="space-y-1">
                  {selectedPositionIds.map(posId => {
                    const position = positions.find(p => p.id === posId);
                    return position ? (
                      <div key={posId} className="text-sm">
                        • {position.name} {position.category ? <span className="text-muted-foreground">({position.category})</span> : ''}
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="bulk-target-position" className="text-sm font-medium">
                Target Position (Active) <span className="text-destructive">*</span>
              </label>
              <select
                id="bulk-target-position"
                value={bulkMapTargetId}
                onChange={(e) => setBulkMapTargetId(e.target.value)}
                className="w-full p-2 border border-input rounded-md bg-background"
                disabled={bulkMapping}
              >
                <option value="">Select a canonical position...</option>
                {positions
                  .filter(p => p.status === 'active' && !selectedPositionIds.includes(p.id))
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(position => (
                    <option key={position.id} value={position.id}>
                      {position.name} {position.category ? `(${position.category})` : ''}
                    </option>
                  ))}
              </select>
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                <strong>This will:</strong>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Set all selected positions to "mapping" status</li>
                  <li>Map them to the chosen canonical position</li>
                  <li>Future references will use the canonical position</li>
                </ul>
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowBulkMapDialog(false)}
              disabled={bulkMapping}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkMap}
              disabled={bulkMapping || !bulkMapTargetId}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {bulkMapping ? (
                <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Mapping...
                </>
              ) : (
                <>
                  <GitMerge className="h-4 w-4 mr-2" />
                  Map {selectedPositionIds.length} Position{selectedPositionIds.length > 1 ? 's' : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PositionsManagement;
